import React, { useState, useEffect } from 'react';
import {
    Bell, Check, CheckCheck, Loader2, X,
    Laptop, Speaker, Baby, Home, ToyBrick, Tag, Shirt, Sparkles, Dumbbell,
    Refrigerator, ShoppingCart, Wrench, PawPrint, Heart, Hammer, Car, Truck,
    Building2, Factory, Briefcase, LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/apiBase';
import { registerPushNotifications } from '../utils/pushNotifications';
import { useAuth } from '../context/AuthContext';

const DISMISS_KEY = 'sv_notif_onboarding_v1';

const ICONS = {
    Laptop, Speaker, Baby, Home, ToyBrick, Tag, Shirt, Sparkles, Dumbbell,
    Refrigerator, ShoppingCart, Wrench, PawPrint, Heart, Hammer, Car, Truck,
    Building2, Factory, Briefcase,
};

// Modal mostrado UMA vez apos o cadastro: o usuario escolhe de quais categorias
// quer receber notificacoes/promocoes. Pode trocar depois em /perfil?tab=notificacoes.
function NotifOnboardingModal({ token, onClose }) {
    const [cats, setCats] = useState([]);
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetch(`${API}/categories`).then(r => r.json()).then(d => setCats(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);

    const toggle = (slug) => setSelected(s => s.includes(slug) ? s.filter(x => x !== slug) : [...s, slug]);

    const allSelected = cats.length > 0 && selected.length === cats.length;
    const toggleAll = () => setSelected(allSelected ? [] : cats.map(c => c.slug));

    const dismiss = () => { try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {} onClose && onClose(); };

    const finish = async (enablePush) => {
        setSaving(true);
        try {
            if (enablePush) {
                const res = await registerPushNotifications(token);
                if (res?.subscribed) toast.success('Notificacoes ativadas!');
                else if (res?.permission === 'denied') toast.error('Permissao negada — libere nas configuracoes do navegador.');
                else if (res?.error === 'VAPID key missing') toast.error('Push indisponivel no momento.');
            }
            await fetch(`${API}/push/preferences`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ categories: selected, web_push: !!enablePush }),
            });
            if (selected.length) toast.success('Categorias salvas!');
        } catch (_) { /* ignore */ }
        dismiss();
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} data-testid="notif-onboarding">
            <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)', maxHeight: '90vh' }}>
                {/* Header */}
                <div className="relative px-5 py-5 text-center" style={{ background: 'linear-gradient(135deg, var(--gold-3) 0%, var(--gold-2) 100%)' }}>
                    <button onClick={dismiss} className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.15)' }} aria-label="Fechar" data-testid="notif-onboarding-close">
                        <X size={16} style={{ color: '#0A0A0A' }} />
                    </button>
                    <Bell size={28} style={{ color: '#0A0A0A' }} className="mx-auto mb-2" />
                    <h2 className="text-lg font-bold" style={{ color: '#0A0A0A' }}>O que você quer acompanhar?</h2>
                    <p className="text-xs mt-1" style={{ color: 'rgba(0,0,0,0.7)' }}>
                        Escolha as categorias para receber ofertas e promoções na hora.
                    </p>
                </div>

                {/* Categorias */}
                <div className="p-4 overflow-y-auto" style={{ maxHeight: '50vh' }}>
                    {/* Barra: contador + selecionar todas */}
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
                            {selected.length > 0 ? `${selected.length} selecionada${selected.length > 1 ? 's' : ''}` : 'Toque para escolher'}
                        </span>
                        <button
                            onClick={toggleAll}
                            className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors"
                            style={{ background: 'var(--surface-2)', color: 'var(--gold-2)', border: '1px solid var(--border)' }}
                            data-testid="onb-select-all"
                        >
                            <CheckCheck size={13} />
                            {allSelected ? 'Limpar' : 'Selecionar todas'}
                        </button>
                    </div>

                    {cats.length === 0 ? (
                        <div className="flex justify-center py-8"><Loader2 className="animate-spin" style={{ color: 'var(--gold-2)' }} /></div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                            {cats.map(c => {
                                const on = selected.includes(c.slug);
                                const Icon = ICONS[c.icon] || LayoutGrid;
                                return (
                                    <button
                                        key={c.category_id || c.slug}
                                        onClick={() => toggle(c.slug)}
                                        className="relative flex flex-col items-center justify-start gap-2 rounded-xl px-1.5 py-3 transition-all"
                                        style={on
                                            ? { background: 'var(--gold-bg)', border: '1.5px solid var(--gold-2)' }
                                            : { background: 'var(--surface-2)', border: '1.5px solid transparent' }}
                                        data-testid={`onb-cat-${c.slug}`}
                                    >
                                        {on && (
                                            <span className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'var(--gold-2)' }}>
                                                <Check size={11} strokeWidth={3} style={{ color: '#0A0A0A' }} />
                                            </span>
                                        )}
                                        <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: on ? 'var(--gold-2)' : 'var(--surface-3)' }}>
                                            <Icon size={20} style={{ color: on ? '#0A0A0A' : 'var(--muted)' }} strokeWidth={1.75} />
                                        </span>
                                        <span className="text-[11px] leading-tight text-center" style={{ color: 'var(--text)', fontWeight: on ? 600 : 400 }}>
                                            {c.name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Acoes */}
                <div className="p-4 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
                    <button
                        onClick={() => finish(true)}
                        disabled={saving}
                        className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                        data-testid="notif-onboarding-enable"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
                        Ativar notificações
                    </button>
                    <button
                        onClick={() => finish(false)}
                        disabled={saving}
                        className="w-full py-2.5 rounded-xl text-sm font-medium"
                        style={{ background: 'var(--surface-2)', color: 'var(--text)' }}
                        data-testid="notif-onboarding-save-only"
                    >
                        Salvar categorias, sem notificações
                    </button>
                    <button onClick={dismiss} className="w-full py-1 text-xs" style={{ color: 'var(--muted-2)' }} data-testid="notif-onboarding-skip">
                        Agora não
                    </button>
                    <p className="text-center text-[10px]" style={{ color: 'var(--muted-2)' }}>
                        <Check size={10} className="inline mr-1" />
                        Você pode mudar isso depois no seu Perfil.
                    </p>
                </div>
            </div>
        </div>
    );
}

// Gate: decide quando mostrar. Aparece quando o usuario esta logado, ainda nao
// passou pelo onboarding (flag local) e ainda nao escolheu categorias no backend.
export default function NotifOnboardingGate() {
    const { user } = useAuth();
    const [show, setShow] = useState(false);
    const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('token') : null;

    useEffect(() => {
        let mounted = true;
        if (!user || !token) { setShow(false); return; }
        let dismissed = false;
        try { dismissed = !!localStorage.getItem(DISMISS_KEY); } catch (_) {}
        if (dismissed) return;
        // So mostra se o usuario ainda nao tem categorias salvas (evita repetir em outro device)
        fetch(`${API}/push/preferences`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (!mounted) return;
                const cats = d?.preferences?.categories;
                if (!cats || cats.length === 0) setShow(true);
                else { try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {} }
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, [user, token]);

    if (!show || !token) return null;
    return <NotifOnboardingModal token={token} onClose={() => setShow(false)} />;
}
